import React, {useEffect, useRef, useState} from "react";
import Box from "../Common/Box";
import shadowClient, {SendFilesOptions} from "../lib/clients/shadow/shadowClient";
import {lighten, useTheme} from "@mui/material/styles";
import ControlButton from "../Common/ControlButton";
import UploadFile from "@mui/icons-material/UploadFile";
import {FileInput} from "../Common/FileInput";
import Logger from "../lib/Logger";
import {createRandomGradient} from "../lib/utils";
import ProgressBar from "../Common/ProgressBar/ProgressBar";
import {AxiosProgressEvent} from "axios";
import Send from "@mui/icons-material/Send";
import BasicInput from "./BasicInput";
import uiStore from "../lib/stores/uiStore";
import Close from "@mui/icons-material/Close";
import {notifier} from "../lib/Notifier";
import {observer} from "mobx-react";
import {Collapse} from "@mui/material";
import {renderToString} from "react-dom/server";
import {parseContent} from "./Message/parseContent";
import CloseRounded from "@mui/icons-material/CloseRounded";

const logger = new Logger("MessageInput3");

const loadImage = async (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    return new Promise((resolve, reject) => {
        reader.addEventListener("load", () => {
            resolve(reader.result as string)
        }, false);
        reader.addEventListener("error", () => {
            reject(reader.error)
        }, false);
    });
};

export default observer(function MessageInput() {
    const controlRef = useRef<{ handleSend: () => void }>();
    const rootRef = useRef(null);
    const [b64Images, setB64Images] = useState([]);
    const [files, setFiles] = useState<File[]>([]);
    const [progress, setProgress] = useState(0);
    const inputRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const users = shadowClient.userStore.users;

    useEffect(() => {
        if (!inputRef.current) {
            return;
        }
        if (uiStore.editedMessage) {
            const {content} = uiStore.editedMessage;

            inputRef.current.innerHTML = renderToString(parseContent(content, users));
            // setCursorToEnd(inputRef.current);
        } else {
            inputRef.current.innerHTML = ""
            // setCursorToEnd(inputRef.current);
        }

    }, [inputRef.current, uiStore.editedMessage]);


    async function onSend(messageContent: string) {
        try {

            const trimmedContent = messageContent.trim();

            if (uiStore.editedMessage) {
                const {room_id, id} = uiStore.editedMessage;
                try {
                    await shadowClient.editMessage({
                        content: trimmedContent
                    }, room_id, id);
                    uiStore.editedMessage = null;
                } catch (e: any) {
                    notifier.error("Failed to edit message. Error: " + e.message);
                }

                return;
            }


            if (files.length > 0) {
                const fileArr = files.map(file => ({
                    blob: file,
                    name: file.name,
                }));

                const options: SendFilesOptions = {
                    progressCallback: (event: AxiosProgressEvent) => {
                        setProgress(event.loaded / event.total);
                    },
                };

                if (messageContent) {
                    options.payloadJson = {
                        content: trimmedContent,
                    }
                }

                setFiles([]);
                await shadowClient.sendFiles(shadowClient.getJoinedRoomId(), fileArr, options);
                setB64Images([]);
                setProgress(0);

            } else {
                if (trimmedContent) {
                    await shadowClient.postMessage(messageContent);
                }

            }
        } catch (e: any) {
            logger.error("Error while sending message. Error: %s " + e?.stack || e || e?.message);
            const errorMessage = "Error while sending message: " + e.message;
            notifier.error(errorMessage);
        }


    }

    const room = shadowClient.getJoinedRoom();
    const placeholder = room ? "Message " + room.name : "Not connected";
    const backgroundColor = "#2e2e2e";
    const paperColor = lighten(backgroundColor, 0.1);
    const ecolor = "#d8d8d8";

    return <Box
            column
            sx={{
                position: "relative"
            }}
    >
        <Collapse
                sx={{
                    width: "100%",
                }}
                in={!!uiStore.editedMessage}
                timeout={200}
        >

            <Box
                    onClick={() => uiStore.editedMessage = null}
                    bgcolor={backgroundColor}
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        height: "35px",
                        fontSize: "14px",
                        color: ecolor
                    }}
            >

                <CloseRounded
                        sx={{
                            padding: "4px",
                            borderRadius: "100%",
                            background: paperColor,
                            width: "20px",
                            height: "20px",
                            margin: "0px 8px",
                            fontSize: "16px",
                            color: ecolor,
                        }}
                />
                Editing Message
            </Box>
        </Collapse>

        <Box
                row
                center
                sx={{
                    position: "relative"
                }}
        >


            <Box
                    ref={rootRef}
                    style={{
                        flex: 1,
                        borderRadius: "5px",
                        border: "1px solid rgba(255, 255, 255, 0.23)",
                        margin: "10px",
                        marginLeft: "15px",
                        position: "relative",
                        padding: "10px",
                    }}
                    row
                    center
                    padding={"10px"}
            >

                <FileInput
                        onClick={async (fileList: FileList) => {
                            const files = Array.from(fileList);

                            for (const file of files) {
                                b64Images.push(await loadImage(file));
                            }

                            setB64Images(b64Images);
                            setFiles(files);
                        }}
                        visible={true}
                        customComponent={() => {
                            // @ts-ignore
                            return <ControlButton
                                    sx={{marginRight: "5px"}}
                                    icon={UploadFile}
                                    width={35}
                                    label={"Upload a file"}
                            />
                        }}
                />
                <BasicInput
                        controlRef={controlRef}
                        rootRef={rootRef}
                        inputRef={inputRef}
                        placeholder={uiStore.editedMessage ? "" : placeholder}
                        onSend={onSend}
                />

            </Box>

            {b64Images.length > 0 && <Box
                    style={{
                        marginTop: "-102px",
                        height: "101px",
                        // left: 0,
                        top: 0,
                        boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.24), 0 3px 1px -2px rgba(0, 0, 0, 0.24), 0 1px 5px 0 rgba(0, 0, 0, 0.2)",
                        position: "absolute",
                        width: "90%",
                        borderRadius: "5px",
                        border: "1px solid rgba(255, 255, 255, 0.13)",
                        background: theme.palette.background.paper,
                        overflow: "hidden",
                    }}
            >
                <Box
                        sx={{
                            height: "100%",
                            overflow: "auto",
                        }}
                >
                    {b64Images.map((img, i) => <Box sx={{
                        position: "relative",
                        display: "inline-flex",
                        width: "33.33333%",
                        aspectRatio: "1 / 1",
                        verticalAlign: "top",
                    }} key={i}>
                        <img
                                key={i}
                                alt={"preview image"}
                                src={img}
                                style={{
                                    height: "100%",
                                    width: "100%",
                                    objectFit: "cover"
                                }}
                        />
                        <ControlButton
                                width={20}
                                sx={{
                                    background: "#454545",
                                    position: "absolute",
                                    top: "8px",
                                    right: "8px",
                                }}
                                onClick={() => {
                                    setFiles(files.filter((_, index) => index !== i));
                                    setB64Images(b64Images.filter((_, index) => index !== i));
                                }}
                                icon={Close}
                        />
                    </Box>)}
                </Box>

                <ProgressBar color={createRandomGradient()} progress={progress}/>
            </Box>}

            {uiStore.isMobile && <>
                <ControlButton
                        sx={{
                            padding: "10px",
                            marginLeft: "-4px",
                            marginRight: "4px",
                        }}
                        aria-label="send"
                        onClick={() => {

                            if (inputRef.current.innerHTML) {
                                inputRef.current.focus();
                            }

                            if (controlRef.current) {
                                controlRef.current.handleSend();
                            }
                        }}
                        icon={Send}
                /></>}
        </Box>
    </Box>
});

